import type { ChatMessage } from "@/types";

/**
 * The newest page of each recently-opened conversation, so reopening one paints
 * immediately instead of after a round trip.
 *
 * Opening a chat used to start from an empty list and fetch, which meant the
 * `ListEmptyComponent` — "You're connected. Break the ice." — was what the
 * reader saw first, every time, in a thread they had been reading a moment
 * before. The sentence is not just early, it is wrong: it says the conversation
 * is empty when the messages are simply still in flight.
 *
 * **In memory only, deliberately.** This is the content of private
 * conversations. Persisting it would make reopening instant after a cold start
 * too, but it would also put plaintext messages on the device where the app has
 * never put them before, outliving the session and surviving "Clear messages"
 * unless every path remembered to sweep it. That is a product and privacy
 * decision, not an implementation detail, so this cache dies with the process
 * and the trade is stated rather than taken.
 *
 * Bounded on both axes, per the repository rule that nothing grows without a
 * limit: at most `MAX_CHATS` conversations, at most `MAX_MESSAGES_PER_CHAT`
 * messages each. `Map` preserves insertion order, and writing re-inserts, so
 * eviction drops the least recently written conversation.
 *
 * Import-free at runtime — the only import is a type, which is erased — so the
 * eviction and clearing rules can be tested without React or the API client.
 */

/** Roughly a session's worth of conversations; the rest cost one fetch. */
export const MAX_CHATS = 20;

/**
 * One page. Holding more would not help: the screen reads a page on open and
 * walks backwards from there on demand, so a deeper cache would go stale
 * without ever being the thing that renders first.
 */
export const MAX_MESSAGES_PER_CHAT = 50;

const cache = new Map<string, ChatMessage[]>();

/**
 * Never null, so a caller can seed `useState` with it directly.
 *
 * An empty result means "nothing worth painting", and deliberately does NOT
 * distinguish "never opened" from "known to be empty" — see `rememberMessages`,
 * which refuses to store an empty page precisely so that distinction cannot be
 * drawn and then acted on.
 */
export function cachedMessages(
  chatId: string | null | undefined,
): ChatMessage[] {
  if (!chatId) return [];
  return cache.get(chatId) ?? [];
}

export function rememberMessages(
  chatId: string | null | undefined,
  messages: ChatMessage[],
): void {
  if (!chatId) return;

  // An empty page is not cached — it is DROPPED. The cache holds pages worth
  // painting, and nothing else.
  //
  // The rejected alternative was to store it and let the screen render the
  // empty state from cache. That trades a correct blank moment for a false
  // assertion: a conversation is empty-and-cached only if you opened it while
  // empty, and the usual reason to reopen it is that the peer has since
  // written. Painting "You're connected. Break the ice." from cache would
  // therefore reinstate the exact flash this cache exists to remove, in the
  // one case it is most likely to be wrong. Stale MESSAGES are fine — five
  // then eight is merely behind. Stale EMPTINESS is a claim about the
  // conversation.
  //
  // Deleting also keeps a page from outliving its content: `load()` writing an
  // empty result (server-side clear, history gone) drops the entry instead of
  // leaving the old page to repaint on the next open.
  if (messages.length === 0) {
    cache.delete(chatId);
    return;
  }

  const page = messages.slice(-MAX_MESSAGES_PER_CHAT);

  // Delete before set so the entry moves to the end of the iteration order;
  // Map.set on an existing key keeps its original position.
  cache.delete(chatId);
  cache.set(chatId, page);

  while (cache.size > MAX_CHATS) {
    const oldest = cache.keys().next();
    if (oldest.done) break;
    cache.delete(oldest.value);
  }
}

/**
 * Drop one conversation — after "Clear messages", or anything else that makes
 * the held page a lie. Leaving it would repaint the cleared thread on reopen.
 */
export function forgetChatMessages(chatId: string | null | undefined): void {
  if (!chatId) return;
  cache.delete(chatId);
}

/** Sign-out. Another account's reader must never see these. */
export function clearChatMessageCache(): void {
  cache.clear();
}

/** Tests only. */
export function cachedChatCount(): number {
  return cache.size;
}
